// NotificationsPage.tsx
import React, { useEffect, useRef, useState } from 'react';
import notificationSelectors from 'store/notification/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import notificationActions from 'store/notification/actions';
import { API, getDataCookie } from 'common';
import moment from 'moment';
import './styles.css';
import ic_clock from '../../assets/clock.svg';
import CalendarPopover, { CalendarPopoverHandle } from 'components/CalendarPopover';
import { AppointmentTypes } from 'views/notification/instance';
import type { Notification, NotificationListQuery } from 'types/models';
const PAGE_SIZE_DEFAULT = 10;

const TABS = [
  { key: 'GENERAL', label: 'General' },
  { key: 'APPOINTMENT_REMINDER', label: 'Reminder'},
  { key: 'APPOINTMENT_CANCELED', label: 'Cancelled'},
];

export default function NotificationsPage() {
  const notifications = useAppSelector(notificationSelectors.notifications);
  // REACT_APP_COOKIE_KEY is always defined at build time (mirrors common.ts's own usage of `!` on env keys).
  const staffId = getDataCookie(process.env.REACT_APP_COOKIE_KEY!);
  const dispatch = useAppDispatch();
  const loaderRef = useRef<HTMLDivElement>(null);
  const calRef = useRef<CalendarPopoverHandle>(null);

  const [activeTab, setActiveTab] = useState(TABS[0].key);
  const isLoadingRef = useRef(false);
  const [params, setParams] = useState<NotificationListQuery>({
    page: 0,
    size: PAGE_SIZE_DEFAULT,
    staffId,
    fromDate: moment().startOf('month').format('MM-DD-YYYY'),
    toDate: moment().endOf('month').format('MM-DD-YYYY'),
    notificationType: undefined
  });

  const fetchData = (params: NotificationListQuery) => {
    const queryParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    );
    setParams(params);
    // Object.fromEntries widens back to a generic string-keyed record; cast reflects the
    // known runtime shape (it was built from a NotificationListQuery) without changing it.
    dispatch(notificationActions.getNotifications.fetch(queryParams as NotificationListQuery));
  };

  const handleChooseRangeDate = async () => {
    // non-null: CalendarPopover is always mounted below before this handler can be invoked
    const {startDate, endDate} = await calRef.current!.open();

    fetchData({
      ...params,
      page: 0,
      fromDate: startDate,
      toDate: endDate,
    });
  }

  const onChangeTab = (tabKey: string) => {
    setActiveTab(tabKey);
    fetchData({
      ...params,
      page: 0,
      notificationType: tabKey === 'GENERAL' ? undefined : tabKey,
    });
  };

  const markAsRead = async (notification: Notification) => {
    if (notification.read) return;

    dispatch(
      notificationActions.setNotifications({
        // non-null: only reachable once notifications has already been fetched (see filteredNotifications below)
        ...notifications!,
        content: notifications!.content.map((n) =>
          n.id === notification.id ? { ...n, read: true } : n
        ),
      })
    );

    try {
      await API.put(`staff-notification/${notification.id}/read?staffId=${staffId}`);
    } catch {
      dispatch(
        notificationActions.setNotifications({
          ...notifications!,
          content: notifications!.content.map((n) =>
            n.id === notification.id ? { ...n, read: false } : n
          ),
        })
      );
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingRef.current && !notifications?.last) {
          isLoadingRef.current = true;
          setParams(prev => {
            const nextPage = prev.page + 1;
            fetchData({
              ...prev,
              page: nextPage
            });
            return {
              ...prev,
              page: nextPage
            };
          });
          setTimeout(() => {
            isLoadingRef.current = false;
          }, 500);
        }
      },
      { threshold: 0.1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
      observer.disconnect();
    };
  }, [dispatch, notifications?.last]);

  useEffect(() => {
    dispatch(
      notificationActions.getNotifications.fetch({
        page: 0,
        size: PAGE_SIZE_DEFAULT,
        staffId,
        fromDate: moment().startOf('month').format('MM-DD-YYYY'),
        toDate: moment().endOf('month').format('MM-DD-YYYY'),
      })
    );
    dispatch(notificationActions.countNotificationUnread.fetch({ staffId }));
  }, []);

  const filteredNotifications = (notifications?.content ?? []).filter(() => true);

  const groupNotificationsByDate = (notificationList: Notification[]) => {
    const grouped: Record<string, Notification[]> = {};
    notificationList.forEach((notification) => {
      const date = moment(notification.createDate).format('MM-DD-YYYY');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(notification);
    });

    const sortedDates = Object.keys(grouped).sort(
      // Moment's `-` relies on implicit valueOf() coercion (both sides are objects, not numbers to TS) —
      // `as any` preserves that exact runtime subtraction without changing behavior.
      (a, b) => (moment(b, 'MM-DD-YYYY') as any) - (moment(a, 'MM-DD-YYYY') as any)
    );

    return sortedDates.map((date) => ({ date, notifications: grouped[date] }));
  };

  const formatDateForDisplay = (dateStr: string) => {
    const date = moment(dateStr, 'MM-DD-YYYY');
    const today = moment().format('MM-DD-YYYY');
    const yesterday = moment().subtract(1, 'day').format('MM-DD-YYYY');

    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    return date.format('DD MMM YYYY');
  };

  const groupedNotifications = groupNotificationsByDate(filteredNotifications);

  return (
    <div className="notifications-page">
      <div className='notification-header-container'>
        <div className="notifications-header">
          <div onClick={() => window.history.back()} style={{ cursor: 'pointer'}}>
            <p>Back</p>
          </div>
          <h1>Notifications</h1>
          <div style={{width: 50}}></div>
        </div>

        <button className="ctb-btn" onClick={() => handleChooseRangeDate()}>
          <span className="ctb-content">
            <span className="ctb-value">
              {params.fromDate}
              <span className="ctb-arrow"> → </span>
              {params.toDate}
            </span>
          </span>
        </button>
        <div className="tab-bar">
          {AppointmentTypes.map((tab) => (
            <div
              key={tab.key}
              className={`tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => onChangeTab(tab.key)}
            >
              {tab.label}
              {tab.badge && <span className="tab-badge">{tab.badge}</span>}
            </div>
          ))}
        </div>
      </div>

      <CalendarPopover
        ref={calRef}
        onSave={() => undefined}
      />

      <div className="notification-list">
        {filteredNotifications.length === 0 && (
          <div className="notification-empty">No notifications</div>
        )}

        {groupedNotifications.map((group) => (
          <div key={group.date}>
            <div className="notification-date-header">
              {formatDateForDisplay(group.date)}
            </div>
            {group.notifications.map((notification) => {
              const tab = AppointmentTypes.find((t) => t.key === notification.notificationType);
              return (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.read ? 'unread' : 'read'}`}
                  onClick={() => markAsRead(notification)}
                >
                  <div className="notification-body">
                    <div className="notification-row">
                      <span
                        className="notification-badge"
                        style={{
                          backgroundColor: tab?.backgroundColor,
                          color: tab?.color,
                        }}
                      >
                        {tab?.label || 'General'}
                      </span>

                      <div className="notification-meta-container">
                        <img width={12} height={12} src={ic_clock} alt="clock" />
                        <p className="notification-meta">
                          {moment().format('MM-DD-YYYY HH:mm A') !==
                          moment(notification.createDate).format('MM-DD-YYYY HH:mm A')
                            ? moment(notification.createDate).format('MM-DD-YYYY HH:mm A')
                            : 'Just now'}
                        </p>
                      </div>
                    </div>
                    <p className="notification-title">{notification.title}</p>
                    <p className="notification-desc">{notification.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {filteredNotifications?.length > 0 && !notifications?.last && (
          <div ref={loaderRef} className="notification-loader">Loading more...</div>
        )}
      </div>
    </div>
  );
}
